import { Category } from "@CommonApi/categories";
import { RoutePostMerchflowParseVariantRestrictions } from "@CommonApi/merchflow/parse-variant-restrictions";
import {
  RoutePostMerchflowSaveVariantRestrictions,
  SaveVariantRestrictionGroup,
} from "@CommonApi/merchflow/save-variant-restrictions";
import { ResponseGetMerchflowStoreFacets } from "@CommonApi/merchflow/store-facets";
import { useAtom } from "jotai";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "src/api";
import { useCategoryRacksQuery } from "src/api/categoryRacks";
import { useLoaders } from "src/components/InlineLoader";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts";
import {
  Button,
  Color,
  Flex,
  Grid,
  HorizontalDivider,
  Icon,
  Modal,
  Scroller,
  Text,
  Textarea,
} from "src/elements";
import { ConflictPanel } from "./components/ConflictPanel";
import { GroupCard } from "./components/GroupCard";
import { conflictedRuleIds, ConflictSideKey, findRuleConflicts, RuleConflict } from "./conflicts";
import { rackNamesOf, rackRuleText, shelfRuleOptions } from "./racks";
import { safeParseGroups } from "./schema";
import { describeVariantRestrictionModalDataAtom } from "./store/atoms";
import {
  ALL_BAYS_SHELVES,
  countScopedStores,
  DraftGroup,
  DraftRule,
  emptyStoreFilter,
  parseStoreCodes,
} from "./types";
import { useTranslation } from "src/i18n";

interface Props {
  category: Category;
}

export const ModalDescribeVariantRestriction = () => {
  const [data, setData] = useAtom(describeVariantRestrictionModalDataAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  if (!data) {
    return null;
  }

  return <ModalDescribeVariantRestrictionInner category={data} />;
};

const ModalDescribeVariantRestrictionInner = ({ category }: Props) => {
  const { t } = useTranslation("planogramDelivery");
  const { hasLoader } = useLoaders();
  const { closeModal, openConfirmModal } = useModals();
  const { toast } = useToasts();
  const {
    getMerchflowStoreFacetsApi,
    postMerchflowParseVariantRestrictionsApi,
    postMerchflowSaveVariantRestrictionsApi,
  } = useApi();

  const [description, setDescription] = useState("");
  const [groups, setGroups] = useState<DraftGroup[] | null>(null);
  const [facets, setFacets] = useState<ResponseGetMerchflowStoreFacets | null>(null);
  const [facetsStatus, setFacetsStatus] = useState<"loading" | "ready" | "error">("loading");
  // History of the plain-English descriptions entered, each with an id so the
  // rules it produced can be paired back to it in the review summary. The
  // description box is cleared after each append.
  const [descriptions, setDescriptions] = useState<{ id: string; text: string }[]>([]);

  // The category's racks, offered as shelf choices ("Rack: WIPES RACK", ...).
  const racksQuery = useCategoryRacksQuery(category.id);
  const rackNames = useMemo(() => rackNamesOf(racksQuery.data), [racksQuery.data]);
  const shelfOptions = useMemo(() => shelfRuleOptions(rackNames), [rackNames, t]);

  const nextId = useRef(0);
  const makeId = (prefix: string) => `${prefix}-${nextId.current++}`;

  // Load the category's available store-filter values, used to populate the
  // pickers and to resolve/validate each group's store scope. Saving is blocked
  // until this succeeds so restrictions can't be applied against a scope the
  // user hasn't been able to review.
  const loadFacets = useCallback(() => {
    setFacetsStatus("loading");
    getMerchflowStoreFacetsApi({ category_id: String(category.id) })
      .then((response) => {
        if (response) {
          setFacets(response);
          setFacetsStatus("ready");
        } else {
          setFacetsStatus("error");
        }
      })
      .catch(() => setFacetsStatus("error"));
  }, [category.id]);

  useEffect(() => {
    loadFacets();
  }, [loadFacets]);

  const isParsing = hasLoader(RoutePostMerchflowParseVariantRestrictions);
  const isSaving = hasLoader(RoutePostMerchflowSaveVariantRestrictions);

  // "append" adds the parsed groups to what's already there (the default — so
  // you can build up rules over several description passes); "replace" discards
  // the current groups and starts fresh from this description. In append mode a
  // parsed group can target an existing group by name ("add COOKIES to group 1"),
  // in which case its rules are merged into that group instead of forming a new one.
  const generate = async (mode: "append" | "replace") => {
    if (!description.trim()) return;

    const existingGroupNames =
      mode === "append"
        ? (groups ?? []).map((_, index) =>
            t("variantRestrictionDescribe.groupName", { index: index + 1 }),
          )
        : [];

    const response = await postMerchflowParseVariantRestrictionsApi({
      category_id: String(category.id),
      description: description.trim(),
      existing_group_names: existingGroupNames,
    });

    const parsed = safeParseGroups(response);

    if (!parsed) {
      toast({
        title: t("variantRestrictionDescribe.parseFailedTitle"),
        message: t("variantRestrictionDescribe.parseFailedMessage"),
        type: "WARNING",
      });
      // Don't wipe existing rules on a failed parse; only seed one if empty.
      setGroups((current) => current ?? [emptyGroup(makeId)]);
      return;
    }

    const entered = description.trim();
    const descriptionId = makeId("desc");
    setDescriptions((current) =>
      mode === "append"
        ? [...current, { id: descriptionId, text: entered }]
        : [{ id: descriptionId, text: entered }],
    );

    const parsedDrafts = parsed.map((group) => ({
      targetName: group.target_group_name ?? null,
      draft: {
        id: makeId("group"),
        storeInput: group.store_codes.join(", "),
        filter: group.store_filter,
        unmatchedStores: group.unmatched_stores,
        rules: group.rules.map((rule) => ({
          id: makeId("rule"),
          variant: rule.variant,
          matched: rule.matched,
          suggestion: rule.suggestion,
          bayNumberRule: rule.bay_number_rule,
          shelfRule: rule.shelf_rule,
          condition: rule.condition,
          sourceDescriptionId: descriptionId,
        })),
      } satisfies DraftGroup,
    }));

    setGroups((current) => {
      if (mode !== "append" || !current) {
        return parsedDrafts.map((parsedDraft) => parsedDraft.draft);
      }

      // Copy so we can append rules into a targeted group without mutating state.
      const next = current.map((group) => ({ ...group, rules: [...group.rules] }));
      const byName = new Map(
        next.map((group, index) => [
          t("variantRestrictionDescribe.groupName", { index: index + 1 }),
          group,
        ]),
      );

      const brandNew: DraftGroup[] = [];
      for (const { targetName, draft } of parsedDrafts) {
        const target = targetName ? byName.get(targetName) : undefined;
        if (target) {
          target.rules.push(...draft.rules);
        } else {
          brandNew.push(draft);
        }
      }
      return [...next, ...brandNew];
    });

    // After appending, clear the box so the next description starts a fresh batch.
    if (mode === "append") setDescription("");
  };

  // Discarding is confirmed — Replace throws away current rules and edits.
  const confirmReplace = () => {
    openConfirmModal({
      title: t("variantRestrictionDescribe.confirmReplaceTitle"),
      description: t("variantRestrictionDescribe.confirmReplaceDescription"),
      icon: { name: "alert", color: Color.yellow },
      buttons: [
        {
          id: "back",
          label: t("variantRestrictionDescribe.back"),
          color: "primary",
          variant: "inverted",
        },
        {
          id: "replace",
          label: t("variantRestrictionDescribe.replace"),
          color: "yellow",
          onClick: () => generate("replace"),
        },
      ],
    });
  };

  const updateGroup = (updated: DraftGroup) => {
    setGroups((current) =>
      (current ?? []).map((group) => (group.id === updated.id ? updated : group)),
    );
  };

  const removeGroup = (id: string) => {
    setGroups((current) => (current ?? []).filter((group) => group.id !== id));
  };

  const addGroup = () => {
    setGroups((current) => [...(current ?? []), emptyGroup(makeId)]);
  };

  const allRules = (groups ?? []).flatMap((group) => group.rules);
  const hasRules = allRules.length > 0;
  const hasEmptyVariant = allRules.some((rule) => !rule.variant.trim());
  const hasEmptyTarget = allRules.some(
    (rule) => !rule.bayNumberRule.trim() || !rule.shelfRule.trim(),
  );
  // Block on unresolved store references: an empty store scope is expanded to
  // every store in the category, so applying an unmatched store would silently
  // widen the restriction store-wide. An intentionally-blank scope is fine.
  const hasUnmatchedStores = (groups ?? []).some((group) => group.unmatchedStores.length > 0);
  // Block on unresolved variants: a rule whose variant didn't match a known
  // variant must be resolved (use the suggestion) or explicitly acknowledged
  // ("Keep as typed") so we don't save a restriction against an unknown variant.
  const hasUnresolvedVariant = allRules.some((rule) => !rule.matched);

  // Contradictory rule pairs — e.g. the same variant both required and forbidden
  // on overlapping shelves in overlapping stores. Both can't be saved, so each
  // pair is surfaced for the user to pick a side, and saving stays blocked until
  // every conflict is resolved.
  const conflicts = useMemo(
    () => findRuleConflicts(groups ?? [], facets?.stores ?? null),
    [groups, facets],
  );
  const conflictedIds = useMemo(() => conflictedRuleIds(conflicts), [conflicts]);
  const hasConflicts = conflicts.length > 0;

  const canApply =
    hasRules &&
    !hasEmptyVariant &&
    !hasEmptyTarget &&
    !hasUnmatchedStores &&
    !hasUnresolvedVariant &&
    !hasConflicts &&
    facetsStatus === "ready" &&
    !isSaving;

  // Saving replaces every existing restriction for the category (the backend
  // does a full delete-then-insert), so confirm before discarding whatever is
  // already there — the modal only shows the newly described rules.
  const apply = () => {
    if (!canApply) return;

    openConfirmModal({
      title: t("variantRestrictionDescribe.confirmApplyTitle", { categoryCode: category.code }),
      description: t("variantRestrictionDescribe.confirmApplyDescription"),
      icon: { name: "alert", color: Color.yellow },
      buttons: [
        {
          id: "back",
          label: t("variantRestrictionDescribe.back"),
          color: "primary",
          variant: "inverted",
        },
        {
          id: "replace",
          label: t("variantRestrictionDescribe.replace"),
          color: "yellow",
          onClick: performApply,
        },
      ],
    });
  };

  const performApply = async () => {
    if (!groups?.length) return;

    const payloadGroups: SaveVariantRestrictionGroup[] = groups
      .filter((group) => group.rules.length > 0)
      .map((group, index) => ({
        variant_group_id: `${category.code}-${index + 1}`,
        store_codes: parseStoreCodes(group.storeInput),
        store_filter: group.filter,
        rules: group.rules.map((rule) => ({
          variant: rule.variant.trim(),
          bay_number_rule: rule.bayNumberRule.trim(),
          shelf_rule: rule.shelfRule.trim(),
          condition: rule.condition,
        })),
      }));

    const response = await postMerchflowSaveVariantRestrictionsApi({
      category_id: String(category.id),
      groups: payloadGroups,
    });

    if (response?.message) {
      toast({
        title: t("variantRestrictionDescribe.savedTitle"),
        message: response.message,
        type: "SUCCESS",
      });
      closeModal("ModalDescribeVariantRestriction");
    }
  };

  // Read-only condition label for the review summary.
  const conditionLabel = (condition: DraftRule["condition"]) => {
    switch (condition) {
      case "MUST_HAVE":
        return t("variantRestrictionDescribe.conditionMustHave");
      case "CAN_HAVE":
        return t("variantRestrictionDescribe.conditionMayHave");
      case "MUST_HAVE_ONLY":
        return t("variantRestrictionDescribe.conditionOnlyThese");
      case "MUST_NOT_HAVE":
        return t("variantRestrictionDescribe.conditionMustNotHave");
    }
  };

  // Spell the "*" wildcard out in the summary ("all bays" rather than "Bay *"),
  // since the summary is what gets proof-read before a destructive save.
  const targetLabel = (rule: DraftRule) => {
    const bay = rule.bayNumberRule.trim();
    const shelf = rule.shelfRule.trim();
    const bayText =
      bay === ALL_BAYS_SHELVES
        ? t("variantRestrictionDescribe.everyBay")
        : `${t("variantRestrictionDescribe.bay")} ${bay || "—"}`;
    const shelfText =
      shelf === ALL_BAYS_SHELVES
        ? t("variantRestrictionDescribe.everyShelf")
        : `${t("variantRestrictionDescribe.shelf")} ${shelf || "—"}`;
    return `${bayText} · ${shelfText}`;
  };

  const ruleLine = (rule: DraftRule) =>
    rackRuleText(rule) ??
    `${rule.variant.trim() || "—"} · ${conditionLabel(rule.condition)} · ${targetLabel(rule)}`;

  // Resolving a conflict = keeping one side; the other rule is removed outright.
  const keepConflictSide = (conflict: RuleConflict, keep: ConflictSideKey) => {
    const removeId = keep === "a" ? conflict.b.rule.id : conflict.a.rule.id;
    setGroups((current) =>
      (current ?? []).map((group) => ({
        ...group,
        rules: group.rules.filter((rule) => rule.id !== removeId),
      })),
    );
  };

  // Each group's resolved store scope, spelled out so the summary shows which
  // stores a group's rules will actually hit (count + codes + active filters).
  const groupScopeLines = (group: DraftGroup): string[] => {
    const lines: string[] = [];

    if (facets) {
      const scoped = countScopedStores(group.storeInput, group.filter, facets.stores);
      lines.push(
        scoped.all
          ? t(
              scoped.count === 1
                ? "variantRestrictionDescribe.appliesToAllStore"
                : "variantRestrictionDescribe.appliesToAllStores",
              { count: scoped.count },
            )
          : t(
              scoped.count === 1
                ? "variantRestrictionDescribe.appliesToStore"
                : "variantRestrictionDescribe.appliesToStoresCount",
              { count: scoped.count },
            ),
      );
    }

    const codes = parseStoreCodes(group.storeInput);
    if (codes.length) {
      lines.push(`${t("variantRestrictionDescribe.appliesToStores")}: ${codes.join(", ")}`);
    }

    const { bay_sizes, states, clusters, section_heights, section_widths } = group.filter;
    if (bay_sizes.length) {
      lines.push(`${t("variantRestrictionDescribe.baySize")}: ${bay_sizes.join(", ")}`);
    }
    if (states.length) {
      lines.push(`${t("variantRestrictionDescribe.state")}: ${states.join(", ")}`);
    }
    if (clusters.length) {
      lines.push(`${t("variantRestrictionDescribe.cluster")}: ${clusters.join(", ")}`);
    }
    if (section_heights.length) {
      lines.push(`${t("variantRestrictionDescribe.sectionHeight")}: ${section_heights.join(", ")}`);
    }
    if (section_widths.length) {
      lines.push(`${t("variantRestrictionDescribe.sectionWidth")}: ${section_widths.join(", ")}`);
    }

    return lines;
  };

  // Pair rules back to the description that produced them — reads the current,
  // edited rules, so the summary updates live. Rules are bucketed by store group
  // so the group is named once instead of repeated on every line.
  const groupedRules = (predicate: (rule: DraftRule) => boolean) =>
    (groups ?? []).flatMap((group, groupIndex) => {
      const matching = group.rules.filter(predicate);
      return matching.length ? [{ groupIndex, rules: matching }] : [];
    });

  const rulesForDescription = (descriptionId: string) =>
    groupedRules((rule) => rule.sourceDescriptionId === descriptionId);
  // Rules added by hand have no source description.
  const manualRules = groupedRules((rule) => !rule.sourceDescriptionId);

  // One demarcated subsection per store group: the group name, the scope its
  // rules apply to, then the rules themselves.
  const translatedCell = (buckets: { groupIndex: number; rules: DraftRule[] }[]) => (
    <Flex column gap="10px">
      {buckets.length > 0 ? (
        buckets.map(({ groupIndex, rules }) => {
          const group = (groups ?? [])[groupIndex];
          return (
            <Flex
              key={groupIndex}
              column
              gap="4px"
              padding="6px 8px"
              background={Color.lightGray}
              borderRadius="4px"
            >
              <Text variant="body2" color={Color.textMain}>
                {t("variantRestrictionDescribe.groupName", { index: groupIndex + 1 })}
              </Text>

              {group &&
                groupScopeLines(group).map((line) => (
                  <Text key={line} variant="small2" color={Color.textSecondary}>
                    {line}
                  </Text>
                ))}

              <Flex column gap="2px" padding="2px 0 0 0">
                {rules.map((rule) => (
                  <Text key={rule.id} variant="body3">
                    {ruleLine(rule)}
                  </Text>
                ))}
              </Flex>
            </Flex>
          );
        })
      ) : (
        <Text variant="body3" color={Color.textSecondary}>
          {t("variantRestrictionDescribe.noRulesForDescription")}
        </Text>
      )}
    </Flex>
  );

  return (
    <Modal
      name="ModalDescribeVariantRestriction"
      title={t("variantRestrictionDescribe.modalTitle", { categoryCode: category.code })}
      width="640px"
    >
      <Scroller>
        <Flex padding="12px" column gap="14px">
          <Textarea
            title={t("variantRestrictionDescribe.descriptionTitle")}
            value={description}
            setValue={setDescription}
            minHeight="80px"
          />

          <Flex justify="between" align="center" gap="12px">
            <Text variant="caption2" color={Color.textSecondary}>
              {groups
                ? t("variantRestrictionDescribe.appendHelp")
                : t("variantRestrictionDescribe.placeholder")}
            </Text>

            <Flex gap="8px" align="center">
              {groups && (
                <Button
                  name="replace-restrictions"
                  variant="inverted"
                  onClick={confirmReplace}
                  isLoading={isParsing}
                  isDisabled={!description.trim() || isParsing}
                >
                  {t("variantRestrictionDescribe.replaceAll")}
                </Button>
              )}

              <Button
                name="generate-restrictions"
                iconLeft={{ name: "ai" }}
                onClick={() => generate(groups ? "append" : "replace")}
                isLoading={isParsing}
                isDisabled={!description.trim() || isParsing}
              >
                {t(
                  groups
                    ? "variantRestrictionDescribe.addToRules"
                    : "variantRestrictionDescribe.generateRules",
                )}
              </Button>
            </Flex>
          </Flex>

          {groups && (
            <Flex column gap="10px">
              <Text variant="small1" color={Color.spaceGray}>
                {hasRules
                  ? t("variantRestrictionDescribe.reviewRules")
                  : t("variantRestrictionDescribe.noRules")}
              </Text>

              {groups.map((group, index) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  index={index}
                  canRemove={groups.length > 1}
                  facets={facets}
                  shelfOptions={shelfOptions}
                  conflictedRuleIds={conflictedIds}
                  makeRuleId={() => makeId("rule")}
                  onChange={updateGroup}
                  onRemove={removeGroup}
                />
              ))}

              {hasConflicts && (
                <ConflictPanel
                  conflicts={conflicts}
                  ruleLabel={ruleLine}
                  onKeep={keepConflictSide}
                />
              )}

              {hasRules && (
                <Flex
                  column
                  gap="8px"
                  padding="10px"
                  borderColor={Color.lightGrayHover}
                  borderRadius="4px"
                >
                  <Text variant="body2" color={Color.textMain}>
                    {t("variantRestrictionDescribe.summary")}
                  </Text>

                  {/* Row-paired table: each described input sits beside the rules
                      it produced, separated by dividers so the two columns can be
                      compared line for line. Reads the current (edited) rules, so
                      it updates live. Rules added by hand have no source
                      description and get a final row. */}
                  <Flex column>
                    <Grid columns="1fr 1fr" gap="16px" align="top" padding="0 0 6px 0">
                      <Text variant="small1" color={Color.textSecondary}>
                        {t("variantRestrictionDescribe.described")}
                      </Text>
                      <Text variant="small1" color={Color.textSecondary}>
                        {t("variantRestrictionDescribe.translatedTo")}
                      </Text>
                    </Grid>

                    <HorizontalDivider />

                    {descriptions.map((desc) => (
                      <Fragment key={desc.id}>
                        <Grid columns="1fr 1fr" gap="16px" align="top" padding="8px 0">
                          <Text variant="body3">{desc.text}</Text>
                          {translatedCell(rulesForDescription(desc.id))}
                        </Grid>
                        <HorizontalDivider />
                      </Fragment>
                    ))}

                    {manualRules.length > 0 && (
                      <Grid columns="1fr 1fr" gap="16px" align="top" padding="8px 0">
                        <Text variant="body3" color={Color.textSecondary}>
                          {t("variantRestrictionDescribe.addedManually")}
                        </Text>
                        {translatedCell(manualRules)}
                      </Grid>
                    )}
                  </Flex>
                </Flex>
              )}

              <Flex justify="between" align="center" gap="10px">
                <Button
                  name="add-group"
                  variant="inverted"
                  size="small"
                  iconLeft={{ name: "plus" }}
                  onClick={addGroup}
                >
                  {t("variantRestrictionDescribe.addStoreGroup")}
                </Button>

                <Button
                  name="apply-restrictions"
                  onClick={apply}
                  isLoading={isSaving}
                  isDisabled={!canApply}
                >
                  {t("variantRestrictionDescribe.replaceRestrictions")}
                </Button>
              </Flex>

              {(hasEmptyVariant || hasEmptyTarget) && (
                <Flex gap="6px" align="center">
                  <Icon name="alert" color={Color.yellow} size={13} />
                  <Text variant="caption2" color={Color.yellow}>
                    {t("variantRestrictionDescribe.missingRuleFields")}
                  </Text>
                </Flex>
              )}

              {hasUnmatchedStores && (
                <Flex gap="6px" align="center">
                  <Icon name="alert" color={Color.yellow} size={13} />
                  <Text variant="caption2" color={Color.yellow}>
                    {t("variantRestrictionDescribe.resolveStoreCodes")}
                  </Text>
                </Flex>
              )}

              {hasUnresolvedVariant && (
                <Flex gap="6px" align="center">
                  <Icon name="alert" color={Color.yellow} size={13} />
                  <Text variant="caption2" color={Color.yellow}>
                    {t("variantRestrictionDescribe.resolveVariants")}
                  </Text>
                </Flex>
              )}

              {hasConflicts && (
                <Flex gap="6px" align="center">
                  <Icon name="alert" color={Color.red} size={13} />
                  <Text variant="caption2" color={Color.red}>
                    {t("variantRestrictionDescribe.resolveConflicts")}
                  </Text>
                </Flex>
              )}

              {facetsStatus === "loading" && (
                <Text variant="caption2" color={Color.textSecondary}>
                  {t("variantRestrictionDescribe.loadingStoreScope")}
                </Text>
              )}

              {facetsStatus === "error" && (
                <Flex gap="8px" align="center">
                  <Icon name="alert" color={Color.yellow} size={13} />
                  <Text variant="caption2" color={Color.yellow}>
                    {t("variantRestrictionDescribe.storeScopeLoadError")}
                  </Text>
                  <Button
                    name="retry-facets"
                    variant="borderless"
                    size="small"
                    onClick={loadFacets}
                  >
                    {t("variantRestrictionDescribe.retry")}
                  </Button>
                </Flex>
              )}

              {racksQuery.isPending && (
                <Text variant="caption2" color={Color.textSecondary}>
                  {t("variantRestrictionDescribe.loadingRacks")}
                </Text>
              )}

              {racksQuery.isError && (
                <Flex gap="8px" align="center">
                  <Icon name="alert" color={Color.yellow} size={13} />
                  <Text variant="caption2" color={Color.yellow}>
                    {t("variantRestrictionDescribe.racksLoadError")}
                  </Text>
                  <Button
                    name="retry-racks"
                    variant="borderless"
                    size="small"
                    onClick={() => void racksQuery.refetch()}
                    isLoading={racksQuery.isFetching}
                  >
                    {t("variantRestrictionDescribe.retry")}
                  </Button>
                </Flex>
              )}

              <Text variant="caption2" color={Color.textSecondary}>
                {t("variantRestrictionDescribe.savingReplaces")}
              </Text>
            </Flex>
          )}
        </Flex>
      </Scroller>
    </Modal>
  );
};

const emptyGroup = (makeId: (prefix: string) => string): DraftGroup => ({
  id: makeId("group"),
  storeInput: "",
  filter: emptyStoreFilter(),
  unmatchedStores: [],
  rules: [
    {
      id: makeId("rule"),
      variant: "",
      matched: true,
      bayNumberRule: "",
      shelfRule: "",
      condition: "MUST_NOT_HAVE",
    },
  ],
});
